/**
 * Cross-client sales aggregations built on the deterministic per-client
 * generators. Replace with live Firestore reads (quotes, opportunities,
 * communications) when the backend is wired.
 */
import type { Account } from './accounts';
import { quotesFor, oppsFor, commsFor, reorderSignal, marginPct, isDemoQuery, type Quote, type Opp, type DeepComm, type Sku } from './clientDeepData';

const clientsOnly = (accounts: Account[]) => accounts.filter((a) => a.type !== 'vendor');

export interface RegistryQuote extends Quote { clientId: string; clientName: string; margin: number; marginPct: number; owner: string; }
export function allQuotes(accounts: Account[], cap = 600): RegistryQuote[] {
  if (!isDemoQuery()) return []; // synthetic registry is demo-only; no live source wired yet
  const out: RegistryQuote[] = [];
  for (const a of clientsOnly(accounts)) {
    const mp = marginPct(a.id);
    for (const q of quotesFor(a.id)) {
      out.push({ ...q, clientId: a.id, clientName: a.name, marginPct: mp, margin: Math.round(q.value * mp), owner: 'Fatima' });
      if (out.length >= cap) return out;
    }
  }
  return out;
}

export interface PipelineOpp extends Opp { clientId: string; clientName: string; }
export function allOpps(accounts: Account[], cap = 400): PipelineOpp[] {
  if (!isDemoQuery()) return []; // demo-only
  const out: PipelineOpp[] = [];
  for (const a of clientsOnly(accounts)) {
    for (const o of oppsFor(a.id)) {
      out.push({ ...o, clientId: a.id, clientName: a.name });
      if (out.length >= cap) return out;
    }
  }
  return out;
}

export interface LogComm extends DeepComm { clientId: string; clientName: string; }
export function allComms(accounts: Account[], cap = 600): LogComm[] {
  if (!isDemoQuery()) return []; // demo-only
  const out: LogComm[] = [];
  for (const a of clientsOnly(accounts)) {
    for (const c of commsFor(a.id)) {
      out.push({ ...c, clientId: a.id, clientName: a.name });
      if (out.length >= cap) return out;
    }
  }
  return out;
}

export interface FollowUp { clientId: string; clientName: string; kind: 'quote' | 'lead' | 'reorder'; label: string; detail: string; due: string; }
export function followUps(accounts: Account[], cap = 200): FollowUp[] {
  if (!isDemoQuery()) return []; // demo-only
  const out: FollowUp[] = [];
  for (const a of clientsOnly(accounts)) {
    for (const q of quotesFor(a.id)) {
      if (q.status === 'sent' || q.status === 'viewed' || q.status === 'negotiating') {
        out.push({ clientId: a.id, clientName: a.name, kind: 'quote', label: `Follow up on ${q.id}`, detail: `${q.product} · ${q.status} · ${q.age}`, due: q.status === 'negotiating' ? 'Today' : 'This week' });
      }
    }
    for (const o of oppsFor(a.id)) {
      if (o.stage === 'qualified' || o.stage === 'quoted') {
        out.push({ clientId: a.id, clientName: a.name, kind: 'lead', label: `Warm lead: ${o.title}`, detail: `${o.stage} · ${o.owner}`, due: 'This week' });
      }
    }
    const rs = reorderSignal(a.id);
    if (rs.due && rs.sku) out.push({ clientId: a.id, clientName: a.name, kind: 'reorder', label: `Reorder likely: ${rs.sku.sku}`, detail: `Last order ${rs.sku.lastOrder} · ${rs.confidence}% confidence`, due: rs.window });
    if (out.length >= cap) break;
  }
  return out;
}

export interface ReorderCandidate { clientId: string; clientName: string; sku: Sku; window: string; confidence: number; reorders: number; }
export function reorderCandidates(accounts: Account[], cap = 200): ReorderCandidate[] {
  if (!isDemoQuery()) return []; // demo-only
  const out: ReorderCandidate[] = [];
  for (const a of clientsOnly(accounts)) {
    const rs = reorderSignal(a.id);
    if (rs.due && rs.sku) out.push({ clientId: a.id, clientName: a.name, sku: rs.sku, window: rs.window, confidence: rs.confidence, reorders: rs.sku.reorders });
    if (out.length >= cap) break;
  }
  return out.sort((x, y) => y.confidence - x.confidence);
}
